var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { boundMethod } from 'autobind-decorator';
import { config } from 'lib-shared-backend-config';
import noop from 'lodash/noop';
import { v4 as uuidv4 } from 'uuid';
import { analytics } from 'app/utils/segment';
import { Sentry } from 'app/utils/sentry';
import { authManager } from 'utils/auth/authManager';
import { getWorkerConnection } from '../worker/getWorkerConnection';
export class SharedWorkerImplementation {
    constructor() {
        this._worker = getWorkerConnection();
        this._isConnected = false;
        this._onWebSocketClosed = noop;
        this._portId = null;
        this._previousPortId = null;
        this._onPrimaryConnectionChange = noop;
    }
    get isConnected() {
        return this._isConnected;
    }
    get portId() {
        return this._portId;
    }
    postMessage(message) {
        this._worker.port.postMessage(message);
    }
    start() {
        return __awaiter(this, void 0, void 0, function* () {
            const authData = yield authManager.getAuth();
            if (authData === null || authData === void 0 ? void 0 : authData.access_token) {
                this.postMessage({
                    type: 'worker:connect-to-websocket',
                    payload: {
                        appToken: authData === null || authData === void 0 ? void 0 : authData.access_token,
                        websocketUrl: config.url.webSocket,
                    },
                });
            }
            // Notify worker that this instance is disconnecting
            window.addEventListener('beforeunload', () => {
                this.postMessage({
                    type: 'worker:disconnect-port',
                    payload: { id: this._portId },
                });
                this.sendAndWaitForResponse('worker:get-number-of-connections').then((response) => {
                    const { payload } = response;
                    analytics.sharedWorkerTabOrWindowClosed({
                        numberOfInstances: payload.numberOfConnections,
                    });
                });
            });
            // Notify worker when the window / tab is focused (active)
            window.addEventListener('focus', this.onWindowFocusHandler);
        });
    }
    close() {
        this.postMessage({
            type: 'worker:close-websocket',
        });
        this.onError(noop);
        window.removeEventListener('focus', this.onWindowFocusHandler);
    }
    onWindowFocusHandler() {
        this.postMessage({
            type: 'worker:port-activated',
            payload: {
                id: this._portId,
            },
        });
    }
    waitForMessageAndResolve(resolve, predicate) {
        const listener = (event) => {
            try {
                const message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                if (predicate(message)) {
                    this._worker.port.removeEventListener('message', listener);
                    resolve(message);
                }
            }
            catch (e) {
                console.warn('Error parsing worker message', event);
                Sentry.addBreadcrumb({
                    category: 'sharedWorker',
                    message: 'Error parsing worker message',
                    level: 'warning',
                    data: { event },
                });
            }
        };
        this._worker.port.addEventListener('message', listener);
    }
    /**
     * Returns a promise that resolves once a specific message is sent from the
     * websocket.
     *
     * @param type The message type which will be compared against incoming
     *   messages from the websocket.
     */
    waitForMessage(type) {
        return new Promise((resolve) => {
            this.waitForMessageAndResolve(resolve, (message) => (message === null || message === void 0 ? void 0 : message.type) === type);
        });
    }
    send(message) {
        if (this._isConnected) {
            this.postMessage(message);
        }
        else {
            console.warn('WS: ws is not ready');
            Sentry.addBreadcrumb({
                category: 'sharedWorker',
                message: 'WS: ws is not ready',
                level: 'warning',
            });
        }
    }
    sendAndWaitForResponse(type) {
        return new Promise((resolve) => {
            const id = uuidv4();
            this.waitForMessageAndResolve(resolve, (message) => { var _a; return ((_a = message === null || message === void 0 ? void 0 : message.payload) === null || _a === void 0 ? void 0 : _a.requestId) === id; });
            this.postMessage({
                type,
                payload: { requestId: id },
            });
        });
    }
    onClose(handler) {
        this._onWebSocketClosed = handler;
    }
    onMessage(onMessageHandler) {
        this._worker.port.onmessage = (event) => {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            if ('type' in data) {
                switch (data.type) {
                    case 'worker:port-connected':
                        this._portId = data.payload.id;
                        this.sendAndWaitForResponse('worker:get-number-of-connections').then((response) => {
                            const { payload } = response;
                            analytics.sharedWorkerTabOrWindowOpened({
                                numberOfInstances: payload.numberOfConnections,
                            });
                        });
                        break;
                    case 'worker:connected-to-websocket':
                        this._isConnected = true;
                        break;
                    case 'worker:websocket-closed':
                        Sentry.captureMessage('WebSocket: Close event received', {
                            level: 'log',
                            extra: data.payload,
                        });
                        analytics.websocketCloseEventReceived(data.payload);
                        this._isConnected = false;
                        this._onWebSocketClosed();
                        break;
                    case 'worker:primary-port-changed':
                        if (data.payload.id === this._portId &&
                            this._previousPortId !== this.portId) {
                            analytics.sharedWorkerTabOrWindowChanged();
                        }
                        this._onPrimaryConnectionChange(data.payload.id === this._portId);
                        this._previousPortId = data.payload.id;
                        break;
                }
            }
            onMessageHandler(data);
        };
    }
    onError(onErrorHandler) {
        this._worker.onerror = (error) => {
            console.warn('WS: Error event received', error);
            Sentry.addBreadcrumb({
                category: 'sharedWorker',
                message: 'WS: Error event received',
                level: 'warning',
                data: { error },
            });
            onErrorHandler(error);
        };
    }
    onPrimaryConnectionChange(handler) {
        this._onPrimaryConnectionChange = handler;
    }
}
__decorate([
    boundMethod
], SharedWorkerImplementation.prototype, "onWindowFocusHandler", null);
